import { useState, useCallback, useEffect } from 'react'
import { propEq, filter, complement } from 'ramda'

import { api } from 'app/api'
import {
  GET_COMPLEXES,
  convertParams,
  convertResult,
} from 'app/common/api/requests/food/schools/complexes/get'
import { POST_COMPLEX_RESTORE } from 'app/common/api/requests/food/schools/complexes/restore'
import { POST_COMPLEX_TOGGLE_VISIBILITY } from 'app/common/api/requests/food/schools/complexes/toggle_visibility'
import { useCurrencySign } from 'app/common/components-next/currency'
import usePopupErr from 'app/common/hooks/usePopupErr'

import kits2SimpleItems, { type Item } from './kits2SimpleItems'

/**
 *  Данные для простого отображения комплектов (удаленные и скрытые)
 */

type Actions = {
  restore: (id: number, title: string) => Promise<boolean>
}

export default function useKitsSimple(orgId: string): [Array<Item>, boolean, Actions] {
  const [popupErr, popup] = usePopupErr()
  const currency = useCurrencySign()

  const [items, setItems] = useState<Array<Item>>([])
  const [loading, setLoading] = useState(true)

  const handleRequest = useCallback(async () => {
    setLoading(true)
    const [res1, res2] = await Promise.all([
      api.request(GET_COMPLEXES, {
        error: 'Не удалось получить данные по комплектам',
        params: { deleted: true, orgId },
        convertions: { convertParams, convertResult },
      }),
      api.request(GET_COMPLEXES, {
        error: 'Не удалось получить данные по комплектам',
        params: { visible: false, orgId },
        convertions: { convertParams, convertResult },
      }),
    ])
    setLoading(false)

    if (res1 && res2) {
      setItems(kits2SimpleItems({ ...res1, ...res2 }, currency))
    }
  }, [orgId, currency])

  useEffect(() => {
    handleRequest()
  }, [handleRequest])

  const handleRestore = useCallback(
    async (id: number, title: string) => {
      const targetItem = items.find((item) => item.id === id)
      if (!targetItem) {
        popupErr(`Не удалось восстановить комплект «${title}». Комплект не найден`)
        return false
      }

      // если в списке удаленных
      if (targetItem.deleted) {
        const res = await api.request(POST_COMPLEX_RESTORE, {
          error: `Не удалось восстановить комплект «${title}»`,
          requestPathParams: {
            orgId,
            complexId: id,
          },
        })
        if (res) {
          setItems(filter(complement(propEq('id', id))))
          popup(`Комплект «${title}» успешно восстановлен`)
        }
        return Boolean(res)
      }
      // если в списке скрытых
      if (targetItem.invisible) {
        const res = await api.request(POST_COMPLEX_TOGGLE_VISIBILITY, {
          error: `Не удалось восстановить комплект «${title}»`,
          requestPathParams: {
            orgId,
            complexId: id,
          },
        })
        if (res) {
          setItems(filter(complement(propEq('id', id))))
          popup(`Комплект «${title}» успешно восстановлен`)
        }
        return Boolean(res)
      }
      return false
    },
    [orgId, popup, popupErr, items],
  )

  return [
    items,
    loading,
    {
      restore: handleRestore,
    },
  ]
}
